import { motion } from 'framer-motion';
import { FileText, BookText, ShieldCheck, Sparkles, ChevronRight } from 'lucide-react';
import ReadinessRing from './ReadinessRing';

/**
 * WorkspaceMockup — pure-HTML 3-pane mockup of the InkPhD workspace.
 * Used in the hero. Renders LEFT chapter hierarchy, CENTER scientific editor
 * with Vancouver [1][2] citations, RIGHT intelligence panel; floating
 * Readiness Ring overlay anchored to the top-right of the editor.
 */
const chapters = [
  { t: 'I. Introduction', depth: 0, active: false, words: 2840 },
  { t: 'II. Literature Review', depth: 0, active: true, words: 6210 },
  { t: 'Surgical Indications', depth: 1, active: false },
  { t: 'Outcomes vs Mortality', depth: 1, active: false },
  { t: 'III. Methods', depth: 0, active: false, words: 4120 },
  { t: 'IV. Results', depth: 0, active: false, words: 5780 },
  { t: 'V. Discussion', depth: 0, active: false, words: 3950 },
];

const intelligenceTabs = ['Copilot', 'Evidence', 'Citations', 'Reviewer'];

export default function WorkspaceMockup() {
  return (
    <div className="relative w-full">
      {/* Outer frame mimicking a window */}
      <div className="relative rounded-xl border border-slate-200 bg-white shadow-[0_24px_60px_-24px_rgba(15,23,42,0.18)] overflow-hidden">
        {/* Title bar */}
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-100 bg-slate-50/60">
          <span className="w-2.5 h-2.5 rounded-full bg-red-400/70" />
          <span className="w-2.5 h-2.5 rounded-full bg-amber-400/70" />
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/70" />
          <span className="ml-3 text-[11px] font-mono text-slate-500 tracking-tight">
            inkphd.com / workspace / phd-pancreatitis
          </span>
        </div>
        {/* 3-pane grid */}
        <div className="grid grid-cols-[150px_minmax(0,1fr)_160px] md:grid-cols-[180px_minmax(0,1fr)_200px] h-[460px]">
          {/* LEFT — chapter hierarchy */}
          <aside className="border-r border-slate-100 bg-slate-50/40 px-2 py-3 overflow-hidden">
            <div className="px-2 mb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
              Hierarchy
            </div>
            <ul className="space-y-0.5">
              {chapters.map((c, i) => (
                <motion.li key={i}
                  initial={{ opacity: 0, x: -8 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.4 }}
                  viewport={{ once: true }}
                  className={`relative px-2 py-1 text-[11px] flex items-center gap-1 rounded ${
                    c.active
                      ? 'bg-[hsl(212,90%,55%)]/10 text-slate-900 font-semibold border-l-2 border-[hsl(212,90%,55%)] pl-2'
                      : 'text-slate-600'
                  }`}
                  style={{ paddingLeft: c.depth ? 16 : 8 }}>
                  {c.depth > 0 && <span className="text-slate-300">↳</span>}
                  <span className="truncate flex-1">{c.t}</span>
                  {c.words && (
                    <span className="text-[9px] font-mono text-slate-400">{(c.words / 1000).toFixed(1)}k</span>
                  )}
                </motion.li>
              ))}
            </ul>
          </aside>

          {/* CENTER — scientific editor */}
          <main className="relative px-5 py-4 overflow-hidden">
            <div className="flex items-center gap-2 mb-3">
              <FileText className="h-3.5 w-3.5 text-slate-400" strokeWidth={1.5} />
              <span className="text-[11px] font-mono text-slate-500 uppercase tracking-wider">
                Literature Review · 6,210 words
              </span>
            </div>
            <h3 className="font-serif text-[15px] leading-tight font-semibold text-slate-900 mb-2">
              2.3 Surgical management of severe acute pancreatitis
            </h3>
            <div className="space-y-2 text-[11.5px] leading-relaxed text-slate-700">
              <motion.p
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.6 }}
                viewport={{ once: true }}>
                Open necrosectomy was the historical standard, with mortality reported
                between 23 % and 38 % across high-volume centers
                <CitationChip num={1} />
                <CitationChip num={2} />
                . The minimally invasive step-up approach reduced new-onset multi-organ
                failure by an absolute 21 %{' '}
                <span className="font-mono text-[10px] text-slate-500">(95 % CI 8–34, p = 0.002)</span>
                <CitationChip num={3} />.
              </motion.p>
              <motion.p
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ delay: 0.55, duration: 0.6 }}
                viewport={{ once: true }}
                className="text-slate-600">
                Subsequent meta-analyses, however, found no statistically significant
                difference in 90-day mortality
                <CitationChip num={4} />, suggesting that the benefit derives from
                avoided complications rather than survival per se.
              </motion.p>
            </div>
            {/* References footnote */}
            <div className="mt-4 pt-3 border-t border-dashed border-slate-200 text-[10px] font-mono text-slate-500 space-y-0.5">
              <div><span className="text-slate-400">[3]</span> van Santvoort HC, et al. Step-up vs open necrosectomy. <span className="italic">N Engl J Med.</span> 2010.</div>
              <div><span className="text-slate-400">[4]</span> Bakker OJ, et al. Endoscopic vs surgical drainage. <span className="italic">JAMA</span>. 2012.</div>
            </div>
          </main>

          {/* RIGHT — intelligence panel */}
          <aside className="border-l border-slate-100 bg-slate-50/40 px-2 py-3 overflow-hidden">
            <div className="flex gap-1 mb-3 px-1">
              {intelligenceTabs.map((t, i) => (
                <span key={t}
                  className={`text-[9px] uppercase font-semibold tracking-wider px-1.5 py-0.5 rounded ${
                    i === 0 ? 'bg-slate-900 text-white' : 'text-slate-500'
                  }`}>
                  {t}
                </span>
              ))}
            </div>
            <div className="space-y-2">
              <PanelCard icon={Sparkles} label="Copilot" body="What does the evidence say about open vs minimally invasive..." />
              <PanelCard icon={BookText} label="Sources" body="34 chunks indexed · 6 attached PDFs" />
              <PanelCard icon={ShieldCheck} label="Verifier" body="14 of 16 citations verified" tone="ok" />
            </div>
          </aside>
        </div>
      </div>

      {/* Floating Readiness Score — anchored top-right of mockup */}
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.85 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ delay: 0.6, duration: 0.7, ease: 'easeOut' }}
        className="absolute -top-6 -right-6 sm:-top-8 sm:-right-8 hidden sm:block">
        <div className="rounded-2xl border border-black/5 bg-white/85 backdrop-blur-xl px-5 py-4 shadow-[0_20px_50px_-15px_rgba(15,23,42,0.25)]">
          <div className="text-[9px] uppercase tracking-[0.2em] font-mono text-slate-400 mb-1">
            Pre-publish Score
          </div>
          <ReadinessRing target={87} size={132} label="" />
          <div className="mt-1 flex items-center gap-1 text-[10px] font-mono text-emerald-700">
            <ChevronRight className="h-3 w-3" />
            Defense-ready
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function CitationChip({ num }) {
  return (
    <a href="#" className="inline-flex items-center mx-0.5 px-1 py-0 text-[9.5px] font-mono font-semibold rounded
      bg-[hsl(212,90%,55%)]/10 text-[hsl(212,90%,38%)] hover:bg-[hsl(212,90%,55%)]/20 transition-colors no-underline align-middle">
      [{num}]
    </a>
  );
}

function PanelCard({ icon: Icon, label, body, tone }) {
  return (
    <div className={`rounded-md border ${
      tone === 'ok' ? 'border-emerald-200 bg-emerald-50/60' : 'border-slate-200 bg-white'
    } px-2 py-1.5`}>
      <div className="flex items-center gap-1 mb-0.5">
        <Icon className="h-3 w-3 text-slate-500" strokeWidth={1.5} />
        <span className="text-[9px] font-mono uppercase tracking-wider text-slate-500">{label}</span>
      </div>
      <p className="text-[10px] text-slate-700 leading-tight">{body}</p>
    </div>
  );
}
